import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API_CONFIG from '../apiConfig';

export type GenerationType =
  | 'tryon'
  | 'photoshoot'
  | 'fashion-extractor'
  | 'ai-ghost-mannequin'
  | 'wrinkle-removal'
  | 'ai-wrinkle-removal'
  | 'video'
  | 'upscaler'
  | 'ai-poses'
  | 'background-removal'
  | 'background-change'
  | 'ai-designer'
  | 'realistic-tryon'
  | 'image-editing'
  | 'ai-workflow'
  | 'ecommerce-catalogue'
  | 'wear-catalogue';

interface CreditEstimationResult {
    creditCost: number | null; // Float value
    isEstimating: boolean;
    error: string | null;
    canAfford: boolean;
}

interface EstimationResponse {
    success: boolean;
    statusCode: number;
    creditCost: number; // Float value
    message: string;
}

export const useCreditEstimation = (
    generationType: GenerationType,
    debounceDelay: number = 500
): [CreditEstimationResult, (payload: any) => void] => {
    const { currentUser, userCredits } = useAuth();
    const [estimationResult, setEstimationResult] = useState<CreditEstimationResult>({
        creditCost: null,
        isEstimating: false,
        error: null,
        canAfford: true
    });

    // Use useRef instead of useState to prevent infinite loops
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const getEstimationEndpoint = (type: GenerationType): string => {
        switch (type) {
            case 'tryon':
                return API_CONFIG.TRYON_CREDIT_COST_URL;
            case 'photoshoot':
                return API_CONFIG.PHOTOSHOOT_CREDIT_COST_URL;
            case 'fashion-extractor':
                return API_CONFIG.FASHION_EXTRACTOR_CREDIT_COST_URL;
            case 'ai-ghost-mannequin':
                return API_CONFIG.AI_GHOST_MANNEQUIN_CREDIT_COST_URL;
            case 'wrinkle-removal':
            case 'ai-wrinkle-removal':
                return API_CONFIG.AI_WRINKLE_REMOVAL_CREDIT_COST_URL;
            case 'video':
                return API_CONFIG.VIDEO_CREDIT_COST_URL;
            case 'upscaler':
                return API_CONFIG.UPSCALER_CREDIT_COST_URL;
            case 'ai-poses':
                return API_CONFIG.AI_POSES_CREDIT_COST_URL;
            case 'background-removal':
                return API_CONFIG.BACKGROUND_REMOVAL_CREDIT_COST_URL;
            case 'background-change':
                return API_CONFIG.BACKGROUND_CHANGE_CREDIT_COST_URL;
            case 'ai-designer':
                return API_CONFIG.AI_DESIGNER_CREDIT_COST_URL;
            case 'realistic-tryon':
                return API_CONFIG.REALISTIC_TRYON_CREDIT_COST_URL;
            case 'image-editing':
                return API_CONFIG.IMAGE_EDITING_CREDIT_COST_URL;
            case 'ai-workflow':
                return API_CONFIG.AI_WORKFLOW_CREDIT_COST_URL;
            case 'ecommerce-catalogue':
                return API_CONFIG.FASHION_CATALOGUE_CREDIT_COST_URL;
            case 'wear-catalogue':
                return API_CONFIG.WEAR_CATALOGUE_CREDIT_COST_URL;
            default:
                throw new Error(`Unknown generation type: ${type}`);
        }
    };

    const estimateCost = useCallback(async (payload: any) => {
        if (!currentUser?.uid) {
            setEstimationResult(prev => ({
                ...prev,
                creditCost: null,
                error: 'User not authenticated'
            }));
            return;
        }

        // Ensure userId is included in payload
        const payloadWithUserId = {
            ...payload,
            userId: currentUser.uid
        };

        setEstimationResult(prev => ({
            ...prev,
            isEstimating: true,
            error: null
        }));

        try {
            const endpoint = getEstimationEndpoint(generationType);
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payloadWithUserId),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: EstimationResponse = await response.json();

            if (data.success && typeof data.creditCost === 'number') {
                // Ensure creditCost is a positive number for comparison/display
                const normalizedCost = Math.abs(data.creditCost);
                const canAfford = userCredits >= normalizedCost;
                setEstimationResult({
                    creditCost: normalizedCost,
                    isEstimating: false,
                    error: null,
                    canAfford
                });
            } else {
                throw new Error(data.message || 'Failed to get credit cost estimation');
            }
        } catch (error) {
            setEstimationResult({
                creditCost: null,
                isEstimating: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred',
                canAfford: true // Default to true if estimation fails
            });
        }
    }, [currentUser?.uid, generationType, userCredits]);

    const debouncedEstimate = useCallback((payload: any) => {
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            estimateCost(payload);
        }, debounceDelay);

        // Update canAfford immediately based on current user credits
        setEstimationResult(prev => ({
            ...prev,
            canAfford: prev.creditCost !== null ? userCredits >= prev.creditCost : true
        }));
    }, [estimateCost, debounceDelay, userCredits]); // Removed debounceTimer dependency

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Update canAfford when userCredits change
    useEffect(() => {
        if (estimationResult.creditCost !== null) {
            setEstimationResult(prev => ({
                ...prev,
                canAfford: userCredits >= prev.creditCost!
            }));
        }
    }, [userCredits, estimationResult.creditCost]);

    return [estimationResult, debouncedEstimate];
}; 